import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import Layout from "../components/Layout";
import { todayLocalISO } from "../lib/utils";
import { SortableHeader, toggleSort, applySortFn, getPersistedSort } from "../components/SortableHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Clock, Filter, Calendar, Pencil, Trash2, AlertTriangle, ChevronLeft, ChevronRight, BarChart3, UserX, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const TimeEntries = () => {
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myEmployeeId, setMyEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sort, setSort] = useState(() => getPersistedSort("time_entries", "worked_date", "desc"));
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ worked_hours: "", break_hours: "0", overtime_reason: "", check_out_time: "", edit_reason: "" });
  const [deleteEntryId, setDeleteEntryId] = useState(null);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [monthlySummary, setMonthlySummary] = useState(null);

  const [formData, setFormData] = useState({
    employee_id: "",
    worked_date: todayLocalISO(),
    worked_hours: "",
    break_hours: "0",
    overtime_reason: ""
  });

  const monthStart = `${selectedMonth}-01`;
  const monthEnd = (() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m, 0);
    return `${selectedMonth}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const fetchData = useCallback(async () => {
    try {
      if (isEmployee) {
        const [teRes, profileRes, pendingRes] = await Promise.all([
          apiClient.get(`/me/time-entries?start_date=${monthStart}&end_date=${monthEnd}`),
          apiClient.get('/me/profile'),
          apiClient.get('/me/time-entries/pending-review').catch(() => ({ data: { entries: [] } }))
        ]);
        setTimeEntries(teRes.data.time_entries);
        const empId = teRes.data.employee_id || profileRes.data.employee?.employee_id;
        setMyEmployeeId(empId);
        if (profileRes.data.employee) {
          setEmployees([profileRes.data.employee]);
        }
        setPendingEntries(pendingRes.data.entries || []);
      } else {
        const params = [`start_date=${monthStart}`, `end_date=${monthEnd}`];
        if (filterEmployee !== 'all') params.push(`employee_id=${filterEmployee}`);
        const [teRes, empRes, pendingRes] = await Promise.all([
          apiClient.get(`/time-entries?${params.join('&')}`),
          apiClient.get('/employees'),
          apiClient.get('/time-entries/pending-review').catch(() => ({ data: { entries: [] } }))
        ]);
        setTimeEntries(teRes.data.time_entries);
        setEmployees(empRes.data.employees);
        setPendingEntries(pendingRes.data.entries || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, [isEmployee, filterEmployee, monthStart, monthEnd]);

  const fetchMonthlySummary = useCallback(async () => {
    if (isEmployee) return;
    try {
      const res = await apiClient.get(`/time-entries/monthly-summary?month=${selectedMonth}`);
      setMonthlySummary(res.data);
    } catch { /* silent */ }
  }, [selectedMonth, isEmployee]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMonthlySummary(); }, [fetchMonthlySummary]);

  const navigateMonth = (dir) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const netHours = parseFloat(formData.worked_hours) - parseFloat(formData.break_hours || 0);
      
      await apiClient.post('/time-entries', {
        employee_id: isEmployee ? myEmployeeId : formData.employee_id,
        worked_date: formData.worked_date,
        worked_hours: parseFloat(formData.worked_hours),
        break_hours: parseFloat(formData.break_hours || 0),
        overtime_reason: netHours > 10 ? formData.overtime_reason : undefined
      });
      
      setShowAddDialog(false);
      setFormData({
        employee_id: "",
        worked_date: todayLocalISO(),
        worked_hours: "",
        break_hours: "0",
        overtime_reason: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create time entry");
    } finally {
      setSaving(false);
    }
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    // For auto-checkout entries, pre-fill HH:MM from the auto check_out so the employee can correct it
    let checkOutTime = "";
    if (entry.source === "clock_auto" && entry.check_out) {
      try {
        const d = new Date(entry.check_out);
        checkOutTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      } catch { /* ignore */ }
    }
    setEditForm({
      worked_hours: String(entry.worked_hours || 0),
      break_hours: String(entry.break_hours || 0),
      overtime_reason: entry.overtime_reason || "",
      check_out_time: checkOutTime,
      edit_reason: "",
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const endpoint = isEmployee
        ? `/me/time-entries/${editingEntry.entry_id}`
        : `/time-entries/${editingEntry.entry_id}`;
      const payload = {
        break_hours: parseFloat(editForm.break_hours || 0),
        overtime_reason: editForm.overtime_reason || undefined,
        edit_reason: editForm.edit_reason || undefined,
      };
      // For auto-checkout entries with a corrected time, send full check_out ISO and let backend derive worked_hours
      if (editingEntry.source === "clock_auto" && editForm.check_out_time && editingEntry.check_in) {
        const ci = new Date(editingEntry.check_in);
        const [h, m] = editForm.check_out_time.split(":").map(Number);
        const co = new Date(ci);
        co.setHours(h, m, 0, 0);
        if (co < ci) co.setDate(co.getDate() + 1); // overnight
        payload.check_out = co.toISOString();
      } else {
        payload.worked_hours = parseFloat(editForm.worked_hours);
      }
      const res = await apiClient.put(endpoint, payload);
      if (res.data?.pending_approval) {
        toast.success("Edit submitted for manager approval");
      } else {
        toast.success("Time entry updated");
      }
      setShowEditDialog(false);
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update time entry");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmEntry = async (entryId) => {
    try {
      await apiClient.post(`/me/time-entries/${entryId}/confirm`);
      toast.success("Entry confirmed");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to confirm"); }
  };

  const handleApproveEdit = async (entryId) => {
    try {
      await apiClient.post(`/time-entries/${entryId}/approve-edit`);
      toast.success("Edit approved");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
  };

  const handleApplyClockedHours = async (entryId) => {
    try {
      const res = await apiClient.post(`/time-entries/${entryId}/apply-clocked-hours`);
      toast.success(`Set to clocked hours (${res.data.worked_hours}h)`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply clocked hours"); }
  };

  const submitReject = async () => {
    if (!rejectDialog) return;
    try {
      await apiClient.post(`/time-entries/${rejectDialog}/reject-edit`, { reason: rejectReason });
      toast.success("Edit rejected");
      setRejectDialog(null); setRejectReason("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reject"); }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const endpoint = isEmployee ? `/me/time-entries/${entryId}` : `/time-entries/${entryId}`;
      await apiClient.delete(endpoint);
      setDeleteEntryId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete time entry");
    }
  };

  const editNetHours = parseFloat(editForm.worked_hours || 0) - parseFloat(editForm.break_hours || 0);

  // Clock-vs-worked-hours mismatch detection. When both check_in and
  // check_out exist we know the physical elapsed time; a worked_hours
  // materially larger than that span is almost always an employee (or
  // manager) inflating the value after the fact. We flag with a tolerance
  // of 0.05h (~3 min) to absorb minute-level rounding.
  const clockSpanHours = (entry) => {
    if (!entry?.check_in || !entry?.check_out) return null;
    const inMs = new Date(entry.check_in).getTime();
    const outMs = new Date(entry.check_out).getTime();
    if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || outMs <= inMs) return null;
    return (outMs - inMs) / 3600000;
  };
  const OVER_CLOCK_TOLERANCE = 0.05;
  const isOverClock = (entry) => {
    const span = clockSpanHours(entry);
    if (span == null) return false;
    return parseFloat(entry?.worked_hours || 0) > span + OVER_CLOCK_TOLERANCE;
  };

  // Live warning inside the edit dialog: compare the value the user is
  // TYPING against the (unchanged) clock in/out span on the entry being
  // edited. Doesn't block save — we want managers to still be able to
  // record legitimate exceptions (forgot-to-clock-out, offline work,
  // etc.) — just makes the discrepancy impossible to miss.
  const editClockSpan = clockSpanHours(editingEntry);
  const editOverClockDelta = editClockSpan != null
    ? parseFloat(editForm.worked_hours || 0) - editClockSpan
    : 0;
  const editShowOverClockWarning = editClockSpan != null && editOverClockDelta > OVER_CLOCK_TOLERANCE;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const netHours = parseFloat(formData.worked_hours || 0) - parseFloat(formData.break_hours || 0);
  const empName = (id) => employees.find(e => e.employee_id === id)?.name || id;
  const isTerminated = (id) => !!employees.find(e => e.employee_id === id)?.termination_date;

  // Chart data
  const chartData = monthlySummary?.days?.filter(d => d.actual_hours > 0 || d.scheduled_hours > 0).map(d => ({
    date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    actual: d.actual_hours,
    scheduled: d.scheduled_hours,
    emp_worked: d.employees_worked,
    emp_assigned: d.employees_assigned,
  })) || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">Time Entries</h1>
            <p className="text-sm text-slate-500 mt-1">Track worked hours by day</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)} data-testid="te-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-semibold text-[#0A192F] min-w-[140px] text-center" data-testid="te-month-label">{monthLabel}</span>
            <Button variant="outline" size="sm" onClick={() => navigateMonth(1)} data-testid="te-next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <Button className="bg-[#0D9488] hover:bg-[#14B8A6]" onClick={() => setShowAddDialog(true)} data-testid="add-time-entry-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Entry
              </Button>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Log Time Entry</DialogTitle>
                  <DialogDescription>Record hours worked for {isEmployee ? "yourself" : "an employee"}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isEmployee && (
                    <div className="space-y-2">
                      <Label>Employee *</Label>
                      <Select value={formData.employee_id} onValueChange={(val) => setFormData({...formData, employee_id: val})}>
                        <SelectTrigger data-testid="time-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                        <SelectContent>{employees.map(emp => (<SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input type="date" value={formData.worked_date} onChange={(e) => setFormData({...formData, worked_date: e.target.value})} required data-testid="time-date-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Hours Worked *</Label><Input type="number" step="0.5" min="0" max="24" value={formData.worked_hours} onChange={(e) => setFormData({...formData, worked_hours: e.target.value})} required data-testid="time-hours-input" /></div>
                    <div className="space-y-2"><Label>Break Hours</Label><Input type="number" step="0.5" min="0" max="8" value={formData.break_hours} onChange={(e) => setFormData({...formData, break_hours: e.target.value})} data-testid="time-break-input" /></div>
                  </div>
                  {netHours > 0 && (<div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm text-slate-600">Net Hours: <span className="font-semibold text-[#0D9488]">{netHours.toFixed(1)} hrs</span></p></div>)}
                  {netHours > 10 && (<div className="space-y-2"><Label className="text-[#E11D48]">Overtime Reason *</Label><Textarea value={formData.overtime_reason} onChange={(e) => setFormData({...formData, overtime_reason: e.target.value})} required data-testid="time-overtime-input" /></div>)}
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                    <Button type="submit" disabled={saving || !formData.worked_hours || (!isEmployee && !formData.employee_id)} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="time-submit-btn">{saving ? "Saving..." : "Save"}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Charts (owner/manager only) */}
        {!isEmployee && chartData.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#0D9488]" /> Actual vs Scheduled Hours
                </CardTitle>
              </CardHeader>
              <CardContent className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="actual" name="Actual Hrs" fill="#0D9488" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="scheduled" name="Scheduled Hrs" fill="#94A3B8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#0A192F] flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#0D9488]" /> Employee Coverage
                </CardTitle>
              </CardHeader>
              <CardContent className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="emp_worked" name="Employees Worked" fill="#14B8A6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="emp_assigned" name="Employees Assigned" fill="#64748B" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Review Banner */}
        {pendingEntries.length > 0 && (
          <Card className="border-red-200 bg-red-50/40" data-testid="pending-review-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#0A192F]">
                  {isEmployee
                    ? `${pendingEntries.length} unfinished clock-${pendingEntries.length === 1 ? 'in' : 'ins'} need${pendingEntries.length === 1 ? 's' : ''} your review`
                    : `${pendingEntries.length} time ${pendingEntries.length === 1 ? 'entry needs' : 'entries need'} review`}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {isEmployee
                    ? "You forgot to clock out — please confirm your actual hours below."
                    : "Auto-closed entries or pending edit approvals require attention."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}



        {/* Filters + Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {!isEmployee && (
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-employee-select"><SelectValue placeholder="All employees" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (<SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Badge variant="outline" className="ml-auto text-slate-500">{timeEntries.length} entries</Badge>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  {!isEmployee && <TableHead><SortableHeader label="Employee" sortKey="_empName" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "time_entries"))} /></TableHead>}
                  <TableHead><SortableHeader label="Date" sortKey="worked_date" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "time_entries"))} /></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Clock In/Out</TableHead>
                  <TableHead><SortableHeader label="Worked" sortKey="worked_hours" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "time_entries"))} /></TableHead>
                  <TableHead><SortableHeader label="Break" sortKey="break_hours" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "time_entries"))} /></TableHead>
                  <TableHead><SortableHeader label="Net Hours" sortKey="net_hours" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "time_entries"))} /></TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}><div className="h-12 bg-slate-100 rounded animate-pulse"></div></TableCell>
                    </TableRow>
                  ))
                ) : timeEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">No time entries for {monthLabel}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  timeEntries.map(e => ({ ...e, _empName: empName(e.employee_id) })).sort(applySortFn(sort)).map((entry) => (
                    <TableRow key={entry.entry_id} data-testid="time-entry-row" className={(entry.needs_review || entry.pending_approval) ? "bg-red-50/40" : ""}>
                      {!isEmployee && <TableCell className="font-medium text-[#0A192F]">
                        <span className="flex items-center gap-1.5">
                          {empName(entry.employee_id)}
                          {isTerminated(entry.employee_id) && <UserX className="w-3.5 h-3.5 text-[#E11D48]" title="Terminated" />}
                        </span>
                      </TableCell>}
                      <TableCell className="text-slate-600"><Calendar className="w-3 h-3 inline mr-1" />{formatDate(entry.worked_date)}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {entry.check_in ? (
                          <span>
                            {new Date(entry.check_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            <span className="text-slate-300 mx-1">→</span>
                            {entry.check_out ? new Date(entry.check_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                            {entry.needs_review && <Badge className="ml-1.5 text-[10px] bg-red-500 hover:bg-red-500 text-white">Needs Review</Badge>}
                            {entry.pending_approval && <Badge className="ml-1.5 text-[10px] bg-amber-500 hover:bg-amber-500 text-white">Pending Approval</Badge>}
                            {entry.source === 'clock_auto' && !entry.needs_review && !entry.pending_approval && <Badge variant="outline" className="ml-1 text-[10px] text-amber-500 border-amber-300">Auto</Badge>}
                          </span>
                        ) : (
                          <span className="text-slate-300">Manual</span>
                        )}
                        {entry.pending_approval && entry.pending_changes && (
                          <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 space-y-1" data-testid={`pending-detail-${entry.entry_id}`}>
                            <div>
                              Requested: <strong>{entry.pending_changes.worked_hours ?? entry.worked_hours}h worked</strong>
                              {entry.pending_over_clock && <span className="ml-1 text-amber-900 font-medium">· over clock-in span</span>}
                            </div>
                            {entry.pending_reason && (
                              <div className="text-amber-900">
                                Reason: <em>&ldquo;{entry.pending_reason}&rdquo;</em>
                              </div>
                            )}
                            {clockSpanHours(entry) != null && !isEmployee && (
                              <button
                                type="button"
                                onClick={() => handleApplyClockedHours(entry.entry_id)}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0D9488] hover:text-[#0F766E] underline underline-offset-2"
                                data-testid={`apply-clocked-hours-${entry.entry_id}`}
                              >
                                Or set to clocked hours ({clockSpanHours(entry).toFixed(2)}h) →
                              </button>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          {entry.worked_hours} hrs
                          {isOverClock(entry) && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                              title={`Worked hours (${entry.worked_hours}h) exceed the clock-in/out span (${clockSpanHours(entry).toFixed(2)}h). Manual edit or forgotten clock-out.`}
                              data-testid={`over-clock-badge-${entry.entry_id}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              +{(parseFloat(entry.worked_hours) - clockSpanHours(entry)).toFixed(2)}h
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-500">{entry.break_hours || 0} hrs</TableCell>
                      <TableCell className="font-semibold text-[#0D9488]">
                        {entry.net_hours} hrs
                        {entry.net_hours > 10 && (<Badge variant="outline" className="ml-2 text-[#EAB308] border-[#EAB308]">OT</Badge>)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isEmployee && entry.needs_review && (
                            <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8 px-2" onClick={() => handleConfirmEntry(entry.entry_id)} data-testid={`confirm-entry-${entry.entry_id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm
                            </Button>
                          )}
                          {!isEmployee && entry.pending_approval && (
                            <>
                              <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-8 px-2" onClick={() => handleApproveEdit(entry.entry_id)} data-testid={`approve-edit-${entry.entry_id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                              </Button>
                              <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50 h-8 px-2" onClick={() => setRejectDialog(entry.entry_id)} data-testid={`reject-edit-${entry.entry_id}`}>
                                <X className="w-3.5 h-3.5 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEditEntry(entry)} data-testid={`edit-time-${entry.entry_id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteEntryId(entry.entry_id)} data-testid={`delete-time-${entry.entry_id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={(open) => { if (!open) { setShowEditDialog(false); setEditingEntry(null); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingEntry?.source === "clock_auto" ? "Correct Clock-Out Time" : "Edit Time Entry"}</DialogTitle>
              <DialogDescription>
                {editingEntry?.source === "clock_auto"
                  ? "We auto-closed your shift because you forgot to clock out. Set the time you actually left."
                  : `Update hours for ${editingEntry ? formatDate(editingEntry.worked_date) : ""}`}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {editingEntry?.source === "clock_auto" ? (
                <>
                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Clocked In</span>
                      <span className="font-medium text-[#0A192F]">{editingEntry?.check_in ? new Date(editingEntry.check_in).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}</span>
                    </div>
                    {editingEntry?.auto_checkout_reason && (
                      <p className="text-xs text-amber-600 mt-2"><AlertTriangle className="w-3 h-3 inline mr-1" />{editingEntry.auto_checkout_reason}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>What time did you actually clock out? *</Label>
                    <Input type="time" value={editForm.check_out_time} onChange={(e) => setEditForm({ ...editForm, check_out_time: e.target.value })} required data-testid="edit-checkout-time" />
                  </div>
                  <div className="space-y-2">
                    <Label>Break Hours</Label>
                    <Input type="number" step="0.25" min="0" max="8" value={editForm.break_hours} onChange={(e) => setEditForm({ ...editForm, break_hours: e.target.value })} data-testid="edit-time-break-input" />
                  </div>
                </>
              ) : (
                <>
                  {editClockSpan != null && (
                    <div
                      className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
                      data-testid="edit-clock-context"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-slate-700">
                          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Clocked In → Out</p>
                          <p className="mt-0.5">
                            <strong>
                              {new Date(editingEntry.check_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                              <span className="text-slate-400 mx-1.5">→</span>
                              {new Date(editingEntry.check_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </strong>
                            <span className="ml-2 text-slate-500">({editClockSpan.toFixed(2)}h)</span>
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/10 shrink-0"
                          onClick={() => setEditForm({ ...editForm, worked_hours: editClockSpan.toFixed(2) })}
                          data-testid="use-clocked-hours-btn"
                        >
                          Use clocked hours
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Hours Worked *</Label><Input type="number" step="0.5" min="0" max="24" value={editForm.worked_hours} onChange={(e) => setEditForm({...editForm, worked_hours: e.target.value})} required data-testid="edit-time-hours-input" /></div>
                    <div className="space-y-2"><Label>Break Hours</Label><Input type="number" step="0.5" min="0" max="8" value={editForm.break_hours} onChange={(e) => setEditForm({...editForm, break_hours: e.target.value})} data-testid="edit-time-break-input" /></div>
                  </div>
                  {editShowOverClockWarning && (
                    <div
                      className="space-y-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
                      data-testid="edit-over-clock-warning"
                    >
                      <div className="flex gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Hours exceed your clock-in/out window</p>
                          <p className="text-xs mt-0.5">
                            Clocked span: <strong>{editClockSpan.toFixed(2)}h</strong> ·
                            &nbsp;Entered: <strong>{parseFloat(editForm.worked_hours || 0).toFixed(2)}h</strong> ·
                            &nbsp;Over by <strong>+{editOverClockDelta.toFixed(2)}h</strong>
                          </p>
                          <p className="text-xs mt-1 text-amber-700">
                            {isEmployee
                              ? "This edit will be sent to your manager for approval. Please explain why."
                              : "You can still save. The entry will be flagged for review on the row."}
                          </p>
                        </div>
                      </div>
                      {isEmployee && (
                        <div className="space-y-1.5 pl-6">
                          <Label className="text-amber-900 text-xs font-medium">Reason for extra hours *</Label>
                          <Textarea
                            value={editForm.edit_reason}
                            onChange={(e) => setEditForm({ ...editForm, edit_reason: e.target.value })}
                            placeholder="e.g., Forgot to clock in on time, had to finish inventory after close, offline delivery run"
                            className="bg-white border-amber-300 focus-visible:ring-amber-400 text-slate-900 text-sm"
                            rows={2}
                            data-testid="over-clock-reason-input"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {editNetHours > 0 && (<div className="p-3 bg-slate-50 rounded-lg"><p className="text-sm text-slate-600">Net Hours: <span className="font-semibold text-[#0D9488]">{editNetHours.toFixed(1)} hrs</span></p></div>)}
                </>
              )}
              {editNetHours > 10 && (<div className="space-y-2"><Label className="text-[#E11D48]">Overtime Reason *</Label><Textarea value={editForm.overtime_reason} onChange={(e) => setEditForm({...editForm, overtime_reason: e.target.value})} required data-testid="edit-time-overtime-input" /></div>)}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowEditDialog(false); setEditingEntry(null); }}>Cancel</Button>
                <Button
                  type="submit"
                  disabled={
                    saving
                    || (editingEntry?.source === "clock_auto" ? !editForm.check_out_time : !editForm.worked_hours)
                    || (isEmployee && editShowOverClockWarning && !editForm.edit_reason.trim())
                  }
                  className="bg-[#0D9488] hover:bg-[#14B8A6]"
                  data-testid="edit-time-submit"
                >
                  {saving
                    ? "Saving..."
                    : (isEmployee && editShowOverClockWarning ? "Submit for approval" : "Update")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reject Edit Dialog (manager) */}
        <Dialog open={!!rejectDialog} onOpenChange={(open) => { if (!open) { setRejectDialog(null); setRejectReason(""); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Edit?</DialogTitle>
              <DialogDescription>Optionally let the employee know why you're rejecting their edit.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (optional)" data-testid="reject-reason-input" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setRejectDialog(null); setRejectReason(""); }}>Cancel</Button>
                <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={submitReject} data-testid="confirm-reject-btn">Reject Edit</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={!!deleteEntryId} onOpenChange={(open) => { if (!open) setDeleteEntryId(null); }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-2"><AlertTriangle className="w-6 h-6 text-red-500" /></div>
              <DialogTitle className="text-center text-[#0A192F]">Delete Time Entry?</DialogTitle>
              <DialogDescription className="text-center text-slate-500 pt-2">This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-center pt-2">
              <Button variant="outline" onClick={() => setDeleteEntryId(null)} data-testid="confirm-delete-cancel">Cancel</Button>
              <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleDeleteEntry(deleteEntryId)} data-testid="confirm-delete-ok">Delete</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TimeEntries;
